import { Icon } from "@/assets/icons";
import { LoadingSpinner } from "@/components";
import {
  useGetMyNotification,
  useGetUnreadCount,
  useMarkAllAsRead,
} from "@/hooks";
import { MyNotificationDto, MyNotiStatus } from "@/types";
import { cn, formatDateFromNow } from "@/utils";
import { useEffect, useState } from "react";
import { BsCheck } from "react-icons/bs";
import { DetailMyNotificationModal } from "../../MyNotification/DetailMyNotificationModal";

interface Props {
  onClose?: () => void;
}

const ListMyNotification = ({ onClose }: Props) => {
  const {
    notifications,
    isLoading,
    isFetched,
    fetchNextPage,
    hasNextPage,
    isLoadFull,
    isFetching,
  } = useGetMyNotification();
  const { unreadCount, error } = useGetUnreadCount();
  const [notificationId, setNotificationId] = useState<number | null>(null);
  const { markAllAsRead, isPending } = useMarkAllAsRead();

  // Hide body scrollbar
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const handleCloseModalDetail = () => {
    setNotificationId(null);
  };

  const openModalDetail = (notification: MyNotificationDto) => {
    setNotificationId(notification.notification_id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  // Handle scroll to bottom in list noti
  const handleScrollList = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const margin = 20; // call if scroll to bottom with margin 20px

    if (
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - margin
    ) {
      // Load more notifications when scrolled to bottom
      if (hasNextPage && !isLoadFull && !isLoading && !isFetching) {
        fetchNextPage();
      }
    }
  };

  if (error) throw error;

  return (
    <div className="overflow-hidden w-full">
      <header className="px-4 py-2 border-b border-border text-sm font-semibold leading-5.5 flex items-center justify-between">
        通知
        <Icon
          name="close"
          className="cursor-pointer md:hidden hover:opacity-70 transition-opacity duration-200"
          onClick={onClose}
        />
      </header>

      {!!unreadCount && (
        <div className="px-4 py-2 flex items-center justify-end gap-2">
          <button
            className="text-cyan text-xs leading-4.5 cursor-pointer flex items-center gap-1 disabled:opacity-75"
            disabled={isPending}
            onClick={handleMarkAllAsRead}
          >
            {isPending ? "マーキング中..." : "すべて既読にする"}
            <BsCheck className="text-cyan size-4" />
          </button>
        </div>
      )}

      {/* Display skeleton when loading  */}
      {isLoading && <LoadingSpinner className="my-2" />}

      {/* List notification  */}
      <div
        className="md:max-h-[525px] overflow-y-auto"
        onScroll={handleScrollList}
      >
        {notifications.map((notification) => (
          <div
            key={notification.notification_id}
            className={cn(
              "px-4 py-2 space-y-1 hover:bg-grey-200/100 transition-colors duration-100 cursor-pointer",
              notification.status !== MyNotiStatus.READ &&
                "bg-grey-200 border-b border-grey-300"
            )}
            onClick={() => openModalDetail(notification)}
          >
            <div
              className={cn(
                "text-xs leading-4.5 text-black line-clamp-3",
                notification.status !== MyNotiStatus.READ && "font-bold"
              )}
            >
              {notification.notification?.title}
            </div>
            <div className="text-[10px] leading-3 text-light_secondary line-clamp-1 truncate">
              {notification.notification?.description}
            </div>
            <div className="text-[10px] leading-4.5 text-light_placeholder tracking-[0.15px]">
              {formatDateFromNow(notification.created_at)}
            </div>
          </div>
        ))}
      </div>

      {/* Show message no data  */}
      {isFetched && notifications.length === 0 && (
        <div className="p-4 text-sm text-light_secondary text-center">
          通知が見つかりませんでした
        </div>
      )}

      <DetailMyNotificationModal
        open={!!notificationId}
        setOpen={handleCloseModalDetail}
        notificationId={notificationId as number}
      />
    </div>
  );
};

export { ListMyNotification };
